import React, { useEffect, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { MoreHorizontal } from 'lucide-react';
import { findGridRoot, focusGrid, openIconProps } from '../dom';

/**
 * A row's menu, filling its cell. The pointer opens it as any menu; the grid's
 * Enter or Space (a click from code, detail 0) opens it too, with the first
 * item focused so the arrow keys move through it. Closing hands the keys back
 * to the grid.
 */
export const MenuCell: React.FC<{
  label: string;
  /** The menu's items. */
  children: React.ReactNode;
}> = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // The first item takes focus once the open menu has mounted, so the arrow
  // keys move through it.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() =>
      contentRef.current
        ?.querySelector<HTMLElement>('[role="menuitem"]:not([data-disabled])')
        ?.focus()
    );
    return () => cancelAnimationFrame(frame);
  }, [open]);
  return (
    <div className='flex h-9 items-center justify-center'>
      <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {/* The whole cell opens the menu, so it counts as the cell's
              one-click icon for the pointer. */}
          <button
            ref={triggerRef}
            type='button'
            {...openIconProps}
            aria-label={label}
            className='flex h-9 w-full items-center justify-center text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:outline-none'
            onClick={e => {
              if (e.detail === 0) setOpen(true);
            }}
          >
            <MoreHorizontal className='h-4 w-4' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          ref={contentRef}
          align='end'
          onCloseAutoFocus={e => {
            e.preventDefault();
            focusGrid(findGridRoot(triggerRef.current));
          }}
        >
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
